// Read-only view of a template definition: sessions at their day offsets and
// start times, critical flags, tracks, practice and completion-policy
// summaries, audience hints. Rendered from the server's definition only.
import { useT } from '../lib/i18n';
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { ProgramTemplate, TemplatePolicy, TemplateSession } from '@/services/learningService';
import { AssignmentTypeBadge } from './AssignmentTypeBadge';
import { modeLabel } from '../lib/completion';

const modeLabels = (t: (k: string, d: string) => string): Record<string, string> => ({
  in_person: t('training:mode_in_person', 'In person'), virtual: t('training:mode_virtual', 'Virtual'),
  hybrid: t('training:mode_hybrid', 'Hybrid'), self_paced: t('training:mode_self_paced', 'Self-paced'),
});
const policySummary = (t: (k: string, d: string, o?: Record<string, unknown>) => string, p?: TemplatePolicy | null) =>
  p ? `${modeLabel(t, p.mode)} · ${t('training:requirements_n', '{{n}} requirement(s)', { n: (p.requirements || []).length })}` : '';

export const TemplatePreview: React.FC<{ template: ProgramTemplate; className?: string }> = ({ template, className }) => {
  const t = useT();
  const modes = modeLabels(t);
  const sessions: TemplateSession[] = [...(template.definition?.sessions || [])].sort((a, b) => (a.day_offset - b.day_offset) || a.start_time.localeCompare(b.start_time));
  const rp = template.definition?.readiness_policy;
  return (
    <div className={`space-y-3 ${className || ''}`} data-testid="template-preview">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{template.name}</span>
          <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">v{template.version}</span>
          <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{template.platform ? t('training:tpl_platform', 'Platform') : t('training:tpl_yours', 'Yours')}</span>
        </div>
        {template.description && <p className="text-xs text-muted-foreground mt-0.5">{template.description}</p>}
        <div className="text-xs text-muted-foreground mt-0.5">
          {template.program_type || t('training:wiz_no_type', 'No type')} · {t('training:sessions_n', '{{n}} session(s)', { n: sessions.length })}
          {template.definition?.settings?.audience_mode ? ` · ${template.definition.settings.audience_mode}` : ''}
          {template.definition?.settings?.default_assignment_type ? ` · ${template.definition.settings.default_assignment_type}` : ''}
        </div>
        {rp?.enabled && (
          <div className="text-xs text-muted-foreground mt-0.5" data-testid="template-readiness">
            {t('training:readiness', 'Readiness')}: {t('training:rp_summary_pct', '{{pct}}% attendance', { pct: rp.attendance_pct })}
            {rp.require_critical_sessions ? ` · ${t('training:rp_critical_short', 'critical sessions')}` : ''}
            {rp.require_practices ? ` · ${t('training:rp_practices_short', 'practices validated')}` : ''}
            {rp.require_verification ? ` · ${t('training:rp_verification_short', 'verified')}` : ''}
          </div>
        )}
      </div>
      {sessions.length === 0 && <p className="text-sm text-muted-foreground">{t('training:tpl_no_sessions', 'This template has no sessions.')}</p>}
      <ol className="space-y-1.5">
        {sessions.map((s, i) => (
          <li key={`${i}-${s.title}`} className="rounded-md border border-border p-2.5 text-sm space-y-1" data-testid={`template-session-${i}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-foreground">{i + 1}. {s.title}</span>
              {s.critical && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 dark:bg-red-500/[0.16] dark:text-red-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"><AlertTriangle className="h-3 w-3" aria-hidden />{t('training:critical', 'Critical')}</span>}
              {s.assignment_type && <AssignmentTypeBadge type={s.assignment_type} />}
            </div>
            <div className="text-xs text-muted-foreground">
              {t('training:tpl_day_offset', 'Day {{n}}', { n: s.day_offset + 1 })} · {s.start_time} · {t('training:tpl_duration', '{{n}} min', { n: s.duration_minutes })}
              {s.delivery_mode ? ` · ${modes[s.delivery_mode] || s.delivery_mode}` : ''}
            </div>
            {s.description && <p className="text-xs text-muted-foreground">{s.description}</p>}
            {s.objectives && <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">{t('training:objectives', 'Objectives')}:</span> {s.objectives}</p>}
            {s.audience_hint && <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">{t('training:tpl_audience_hint', 'Audience hint')}:</span> {s.audience_hint}</p>}
            {(s.tracks || []).length > 0 && (
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{t('training:tracks_n', '{{n}} track(s)', { n: (s.tracks || []).length })}:</span>{' '}
                {(s.tracks || []).map((tr) => `${tr.name}${tr.assignment_type ? ` (${tr.assignment_type})` : ''}${tr.practice ? ` · ${t('training:practice_bound', 'Practical exercise bound')}` : ''}${tr.policy ? ` · ${policySummary(t, tr.policy)}` : ''}`).join('; ')}
              </div>
            )}
            {(s.practice || s.policy) && (
              <div className="text-xs text-muted-foreground">
                {s.practice ? `${t('training:practice_title', 'Practical exercise')}: ${s.practice.title}` : ''}
                {s.practice && s.policy ? ' · ' : ''}
                {s.policy ? `${t('training:completion_policy', 'Completion policy')}: ${policySummary(t, s.policy)}` : ''}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

export default TemplatePreview;
